import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AuthQuery } from '@app/auth/auth.query';
import { canSeeHelpPage, ModuleVisibility } from '@app/@shared/module-visibility';

export interface HelpItem {
  term: string;
  description: string;
}

export interface HelpSection {
  heading: string;
  items: HelpItem[];
}

export interface HelpPage {
  id: string;
  title: string;
  route: string;
  summary: string;
  sections: HelpSection[];
}

@Component({
  selector: 'app-help',
  templateUrl: './help.component.html',
  styleUrls: ['./help.component.scss'],
})
export class HelpComponent implements OnInit {
  pages: HelpPage[] = [];
  searchTerm = '';
  expanded: Record<string, boolean> = {};
  isLoading = true;
  loadError = false;

  constructor(private http: HttpClient, private authQuery: AuthQuery) {}

  ngOnInit(): void {
    // Build an absolute URL so the ApiPrefixInterceptor (which prefixes relative
    // URLs with environment.serverUrl) does not redirect this to the API server.
    const url = new URL('assets/help/help.de.json', document.baseURI).toString();
    this.http.get<{ pages: HelpPage[] }>(url).subscribe({
      next: (data) => {
        this.pages = data?.pages ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
      },
    });
  }

  get hasSearch(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  get visiblePages(): HelpPage[] {
    return this.pages.filter((page) => canSeeHelpPage(page.id, this.visibility));
  }

  get filteredPages(): HelpPage[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.visiblePages;
    }
    return this.visiblePages.filter((page) => this.pageMatches(page, term));
  }

  private get visibility(): ModuleVisibility {
    return {
      can: (module) => this.authQuery.can(module),
      isSuperuser: this.authQuery.isSuperuser,
      isAdmin: this.authQuery.isAdmin,
      isEditor: this.authQuery.isEditor,
      isErfasser: this.authQuery.isErfasser,
    };
  }

  private pageMatches(page: HelpPage, term: string): boolean {
    if (page.title.toLowerCase().includes(term) || page.summary.toLowerCase().includes(term)) {
      return true;
    }
    return page.sections.some(
      (section) =>
        section.heading.toLowerCase().includes(term) ||
        section.items.some(
          (item) =>
            item.term.toLowerCase().includes(term) || item.description.toLowerCase().includes(term)
        )
    );
  }

  toggle(id: string): void {
    this.expanded[id] = !this.expanded[id];
  }

  isExpanded(page: HelpPage): boolean {
    // While searching, matching panels are auto-expanded.
    return this.hasSearch || !!this.expanded[page.id];
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  trackById(_index: number, page: HelpPage): string {
    return page.id;
  }
}
